import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { log, logError, startTimer } from '../lib/logger';
import { trackMetric } from '../lib/metrics';
import { broadcastTransactionChange } from './useTransactions';

export type AccountType =
  | 'corrente' | 'digital' | 'poupanca' | 'carteira'
  | 'investimento' | 'dinheiro' | 'corretora'
  | 'dolar' | 'euro' | 'outra_moeda' | 'cripto'
  | 'internacional' | 'pj';

export type AccountCurrency = 'BRL' | 'USD' | 'EUR' | 'GBP' | 'ARS' | 'BTC' | 'ETH' | 'USDT';

export interface Account {
  id: string;
  user_id: string;
  name: string;
  nickname: string;
  type: AccountType;
  bank: string;
  bank_name: string;
  bank_code: string;
  branch_number: string;
  account_number: string;
  account_digit: string;
  currency: AccountCurrency;
  initial_balance: number;
  current_balance: number;
  color: string;
  is_active: boolean;
  is_archived: boolean;
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface AccountStats {
  totalIncome: number;
  totalExpense: number;
  incomeCount: number;
  expenseCount: number;
  lastTransaction: string | null;
  calculatedBalance: number;
  recentTransactions: {
    id: string;
    description: string;
    type: 'income' | 'expense';
    amount: number;
    date: string;
    status: string;
    category: string;
    transaction_subtype?: string | null;
  }[];
}

export const useAccounts = (includeArchived = false) => {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Security: clear stale data immediately when user changes or logs out ──
  const prevUserIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const currentUserId = user?.id;
    if (prevUserIdRef.current !== currentUserId) {
      if (prevUserIdRef.current !== undefined || currentUserId === undefined) {
        setAccounts([]);
        setError(null);
      }
      prevUserIdRef.current = currentUserId;
    }
  }, [user?.id]);

  const fetchAccounts = useCallback(async () => {
    if (!user) {
      setAccounts([]);   // Clear stale data on logout
      setLoading(false);
      return;
    }
    setLoading(true);
    let query = supabase
      .from('accounts')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: true });

    if (!includeArchived) {
      query = query.eq('is_archived', false);
    }

    const { data, error: err } = await query;
    if (err) setError(err.message);
    else setAccounts((data as Account[]) ?? []);
    setLoading(false);
  }, [user, includeArchived]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const createAccount = async (
    values: Omit<Account, 'id' | 'user_id' | 'created_at' | 'updated_at' | 'is_archived'>
  ) => {
    if (!user) return { error: 'Não autenticado' };
    const elapsed = startTimer();
    log({ flow: 'createAccount', status: 'start', userId: user.id, entity: 'account', context: { type: values.type } });
    const { error: err } = await supabase.from('accounts').insert({
      ...values,
      user_id: user.id,
      is_archived: false,
    });
    if (err) {
      logError('createAccount', `Erro ao criar conta: ${err.message}`, { userId: user.id, entity: 'account', context: { code: err.code }, durationMs: elapsed() });
      trackMetric('create_account', { flow: 'createAccount', success: false, durationMs: elapsed() });
    } else {
      log({ flow: 'createAccount', status: 'success', userId: user.id, entity: 'account', message: 'Conta criada', durationMs: elapsed() });
      trackMetric('create_account', { flow: 'createAccount', success: true, durationMs: elapsed() });
      await fetchAccounts();
    }
    return { error: err?.message ?? null };
  };

  const updateAccount = async (id: string, values: Partial<Account>) => {
    const { error: err } = await supabase
      .from('accounts')
      .update({ ...values, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('user_id', user?.id ?? '');
    if (!err) await fetchAccounts();
    return { error: err?.message ?? null };
  };

  const archiveAccount = async (id: string) => {
    const { error: err } = await supabase
      .from('accounts')
      .update({ is_archived: true, is_active: false, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('user_id', user?.id ?? '');
    if (!err) {
      // Soft-delete todas as transações pendentes vinculadas a esta conta
      // para evitar que apareçam no dashboard como transações órfãs.
      // Transações já pagas/recebidas são preservadas pois fazem parte do histórico financeiro real.
      await supabase
        .from('transactions')
        .update({ deleted_at: new Date().toISOString(), updated_at: new Date().toISOString() })
        .eq('account_id', id)
        .eq('user_id', user?.id ?? '')
        .in('status', ['pendente', 'vencido', 'atrasado'])
        .is('deleted_at', null);
      // Notifica dashboard para refazer fetch sem cache
      broadcastTransactionChange();
      await fetchAccounts();
    }
    return { error: err?.message ?? null };
  };

  const unarchiveAccount = async (id: string) => {
    const { error: err } = await supabase
      .from('accounts')
      .update({ is_archived: false, is_active: true, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('user_id', user?.id ?? '');
    if (!err) await fetchAccounts();
    return { error: err?.message ?? null };
  };

  const deleteAccount = async (id: string): Promise<{
    error: string | null;
    hasTransactions?: boolean;
    transactionCount?: number;
    sampleTransactions?: Array<{ id: string; description: string; type: string; amount: number; date: string; status: string; category: string }>;
  }> => {
    const { data: txData, count } = await supabase
      .from('transactions')
      .select('id, description, type, amount, date, status, category', { count: 'exact' })
      .eq('account_id', id)
      .eq('user_id', user?.id ?? '')
      .is('deleted_at', null)
      .order('date', { ascending: false })
      .limit(5);

    if ((count ?? 0) > 0) return {
      error: null,
      hasTransactions: true,
      transactionCount: count ?? 0,
      sampleTransactions: (txData ?? []) as Array<{ id: string; description: string; type: string; amount: number; date: string; status: string; category: string }>,
    };

    const { error: err } = await supabase
      .from('accounts')
      .delete()
      .eq('id', id)
      .eq('user_id', user?.id ?? '');
    if (!err) await fetchAccounts();
    return { error: err?.message ?? null, hasTransactions: false };
  };

  const getAccountStats = async (accountId: string): Promise<AccountStats> => {
    const { data: txs } = await supabase
      .from('transactions')
      .select('id, description, type, amount, date, status, category, transaction_subtype')
      .eq('account_id', accountId)
      .eq('user_id', user?.id ?? '')
      .is('deleted_at', null) // ← CRÍTICO: excluir soft-deleted dos stats da conta
      .order('date', { ascending: false });

    const raw = (txs ?? []) as {
      id: string; description: string; type: string;
      amount: number; date: string; status: string; category: string;
      transaction_subtype?: string | null;
    }[];

    // Normalize type: DB stores 'receita'/'despesa' (Portuguese)
    const isIncome = (t: typeof raw[0]) => t.type === 'receita' || t.type === 'income';
    const isExpense = (t: typeof raw[0]) => t.type === 'despesa' || t.type === 'expense';

    // Exclude transfers from the STATS display boxes (Receitas / Despesas)
    // but keep them for the balance calculation so saldo atual is accurate.
    const isTransfer = (t: typeof raw[0]) => (t as unknown as { transaction_subtype?: string }).transaction_subtype === 'transferencia';

    // Stats boxes — exclude transfers
    const paidIncomes = raw.filter(t => isIncome(t) && (t.status === 'pago' || t.status === 'recebido') && !isTransfer(t));
    const paidExpenses = raw.filter(t => isExpense(t) && t.status === 'pago' && !isTransfer(t));
    const totalIncome = paidIncomes.reduce((s, t) => s + Number(t.amount), 0);
    const totalExpense = paidExpenses.reduce((s, t) => s + Number(t.amount), 0);

    // Balance calculation — INCLUDE transfers so saldo atual reflects transfers correctly
    const allPaidIncomes = raw.filter(t => isIncome(t) && (t.status === 'pago' || t.status === 'recebido'));
    const allPaidExpenses = raw.filter(t => isExpense(t) && t.status === 'pago');
    const totalIncomeForBalance = allPaidIncomes.reduce((s, t) => s + Number(t.amount), 0);
    const totalExpenseForBalance = allPaidExpenses.reduce((s, t) => s + Number(t.amount), 0);

    const account = accounts.find(a => a.id === accountId);
    const calculatedBalance = (account?.initial_balance ?? 0) + totalIncomeForBalance - totalExpenseForBalance;

    // Only show paid transactions in the account detail — pending ones are not reflected in the balance
    const paidTransactions = raw.filter(
      t => t.status === 'pago' || t.status === 'recebido'
    );

    return {
      totalIncome, totalExpense,
      incomeCount: paidIncomes.length,
      expenseCount: paidExpenses.length,
      lastTransaction: paidTransactions.length > 0 ? paidTransactions[0].date : null,
      calculatedBalance,
      recentTransactions: paidTransactions.slice(0, 20).map(t => ({
        id: t.id,
        description: t.description,
        type: isIncome(t) ? 'income' : 'expense' as 'income' | 'expense',
        amount: Number(t.amount),
        date: t.date,
        status: t.status,
        category: t.category,
        transaction_subtype: t.transaction_subtype ?? null,
      })),
    };
  };

  const totalBalance = accounts
    .filter(a => !a.is_archived)
    .reduce((sum, a) => sum + (a.current_balance || 0), 0);

  return {
    accounts,
    loading,
    error,
    totalBalance,
    createAccount,
    updateAccount,
    archiveAccount,
    unarchiveAccount,
    deleteAccount,
    getAccountStats,
    refetch: fetchAccounts,
  };
};
